// next-app/src/providers/auth-provider.tsx
'use client';

import { useEffect, type ReactNode } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuthStore, selectIsAuthenticated } from '@/store/auth-store';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth provider that handles hydration and initial auth state.
 * Wraps the entire application to ensure store is hydrated before rendering.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const isHydrated = useAuthStore((state) => state.isHydrated);

  if (!isHydrated) {
    return null;
  }

  return <>{children}</>;
}

/**
 * Hook to protect routes that require authentication.
 * Redirects to /login if not authenticated.
 */
export function useRequireAuth() {
  const router = useRouter();
  const pathname = usePathname();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      // Save the attempted URL for redirect after login
      sessionStorage.setItem('redirectAfterLogin', pathname);
      router.replace('/login');
    }
  }, [isAuthenticated, router, pathname]);

  return { isAuthenticated };
}

/**
 * Hook to redirect authenticated users away from auth pages.
 * Used on login, signup, etc.
 */
export function useRedirectIfAuthenticated(redirectTo: string = '/events') {
  const router = useRouter();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      // Check for saved redirect URL
      const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAfterLogin');
      router.replace(savedRedirect || redirectTo);
    }
  }, [isAuthenticated, router, redirectTo]);

  return { isAuthenticated };
}
